import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Truck, RotateCcw, Award, Sparkles } from "lucide-react";

import api from "@/lib/api";
import ScrollReveal from "@/components/ScrollReveal";
import CountdownTimer from "@/components/CountdownTimer";
import ProductCard from "@/components/ProductCard";
import QuickView from "@/components/QuickView";
import WordReveal from "@/components/WordReveal";
import MagneticButton from "@/components/MagneticButton";
import { useNavigate } from "react-router-dom";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger
} from "@/components/ui/accordion";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/af4d0a77-eb4a-4084-b760-b3d96dc60e11/images/6b5be68f2e2e4bc476e1ce6b8e2844702d8d9d392952b179f61c570f12e70d48.png";
const MEN_IMG = "https://static.prod-images.emergentagent.com/jobs/af4d0a77-eb4a-4084-b760-b3d96dc60e11/images/1d33ebc43675bab2beee1cd33674c5c3efdea835ac89705b4d5b30ec3a6ce26d.png";
const WOMEN_IMG = "https://static.prod-images.emergentagent.com/jobs/af4d0a77-eb4a-4084-b760-b3d96dc60e11/images/8a3ea759775d7f37959ae978ac021fb4066d9f70c5f796c3ace720923239f8d3.png";
const SHIRTS_IMG = "https://static.prod-images.emergentagent.com/jobs/af4d0a77-eb4a-4084-b760-b3d96dc60e11/images/0cc1222e34152a05e322999d1d2dde46262335e5d52f33561d890327db0dc37a.png";

const TESTIMONIALS = [
  { quote: "There is a quiet confidence in everything they make — pieces that whisper rather than shout.", author: "Vogue India", role: "Atelier Feature" },
  { quote: "I have re-ordered the linen shirt three times. It is becoming my uniform.", author: "Aanya Kapoor", role: "Architect, Mumbai" },
  { quote: "Heritage detailing without nostalgia. INDUS EIRENE is what I reach for first.", author: "Harper's Bazaar", role: "Cover Story" },
];

const FAQ = [
  { q: "How is each piece made?", a: "Each garment is hand-finished in our Mumbai atelier with heritage techniques and ethically sourced fabrics." },
  { q: "What is your replacement policy?", a: "7-day atelier replacement, no questions asked. Items must be unworn with original tags." },
  { q: "Do you offer alterations?", a: "Complimentary alterations within 14 days of delivery via our partner studios in 12 cities." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [quick, setQuick] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products", { params: { limit: 8, sort: "newest" } })
      .then(({ data }) => setProducts(data.items || []))
      .catch(() => setProducts([]));
  }, []);

  return (
    <div data-testid="home-page">
      {/* ────────── HERO ────────── */}
      <section className="relative h-[88vh] min-h-[560px] sm:h-[92vh] sm:min-h-[640px] overflow-hidden" data-testid="hero-section">
        <motion.div
          initial={{ scale: 1.08 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0"
        >
          <img src={HERO_IMG} alt="INDUS EIRENE atelier" className="w-full h-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/30 to-black/65" />

        <div className="relative h-full max-w-[1600px] mx-auto px-5 sm:px-6 md:px-10 pb-16 sm:pb-20 flex flex-col justify-end text-white">
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="ie-overline text-white/85 mb-5 sm:mb-6"
          >
            The Equinox Edit · Spring '26
          </motion.div>

          <h1 className="font-serif text-[40px] sm:text-[64px] md:text-[96px] lg:text-[112px] leading-[0.98] tracking-[-0.02em] max-w-5xl">
            <span className="block">
              <WordReveal text="Quietly composed." delay={0.55} />
            </span>
            <span className="block mt-1 sm:mt-2">
              <WordReveal text="Worn for years." delay={0.85} italicWords={["Worn", "for", "years"]} />
            </span>
          </h1>

          <motion.div
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.5, ease: [0.16, 1, 0.3, 1] }}
            className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4"
          >
            <MagneticButton
              onClick={() => navigate("/shop/women")}
              className="ie-btn bg-white text-black border-white hover:bg-transparent hover:text-white"
              data-testid="hero-cta-women"
            >
              Shop Women <ArrowRight className="w-4 h-4 ml-3"/>
            </MagneticButton>
            <MagneticButton
              onClick={() => navigate("/shop/men")}
              className="ie-btn bg-transparent text-white border-white hover:bg-white hover:text-black"
              data-testid="hero-cta-men"
            >
              Shop Men <ArrowRight className="w-4 h-4 ml-3"/>
            </MagneticButton>
          </motion.div>
        </div>

        {/* Floating scroll cue */}
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.9, duration: 1 }}
          className="absolute bottom-6 left-5 sm:left-6 md:left-10 hidden md:flex flex-col items-start gap-3 text-white/70"
        >
          <div className="ie-overline">Scroll · The First Look</div>
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="w-px h-12 bg-white/50"
          />
        </motion.div>

        <div className="absolute bottom-6 right-6 hidden md:block text-white/70 ie-overline">N° 01 · Atelier Edit</div>
      </section>

      {/* ────────── COUNTDOWN STRIP ────────── */}
      <section className="border-y border-border bg-surface" data-testid="countdown-strip">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-8 md:py-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-5 md:gap-6">
          <div>
            <div className="ie-overline text-brand-accent mb-2">The Equinox Edit</div>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl tracking-tight">Limited atelier pieces · Up to 30% privately</h2>
          </div>
          <CountdownTimer />
        </div>
      </section>

      {/* ────────── COLLECTIONS — bento ────────── */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 py-20 md:py-32" data-testid="collections-section">
        <ScrollReveal className="mb-16 md:mb-24 max-w-3xl">
          <div className="ie-overline text-brand-accent mb-4">The Atelier</div>
          <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight">
            Two houses,<br/>one quiet language.
          </h2>
        </ScrollReveal>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-6">
          <ScrollReveal className="md:col-span-7 md:row-span-2 group cursor-pointer">
            <Link to="/shop/women" className="block relative overflow-hidden h-[60vh] md:h-[82vh]" data-testid="collection-women">
              <img src={WOMEN_IMG} alt="Women's collection" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent"/>
              <div className="absolute bottom-8 left-8 right-8 text-white">
                <div className="ie-overline mb-3 opacity-80">N° 02</div>
                <h3 className="font-serif text-4xl md:text-5xl leading-tight">The Women's Atelier</h3>
                <div className="ie-overline mt-4 inline-flex items-center gap-3">Discover <ArrowRight className="w-3 h-3"/></div>
              </div>
            </Link>
          </ScrollReveal>

          <ScrollReveal delay={0.15} className="md:col-span-5">
            <Link to="/shop/men" className="block relative overflow-hidden h-[40vh] md:h-[39vh] group" data-testid="collection-men">
              <img src={MEN_IMG} alt="Men's collection" className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent"/>
              <div className="absolute bottom-6 left-6 text-white">
                <div className="ie-overline opacity-80 mb-2">N° 03</div>
                <h3 className="font-serif text-3xl md:text-4xl">The Men's Atelier</h3>
              </div>
            </Link>
          </ScrollReveal>

          <ScrollReveal delay={0.25} className="md:col-span-5">
            <Link to="/shop?sort=newest" className="block relative overflow-hidden h-[40vh] md:h-[39vh] group bg-brand-sand" data-testid="collection-new">
              <img src={SHIRTS_IMG} alt="New In" className="absolute inset-0 w-full h-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105"/>
              <div className="absolute inset-0 bg-gradient-to-t from-black/45 to-transparent"/>
              <div className="absolute bottom-6 left-6 text-white">
                <div className="ie-overline opacity-80 mb-2">N° 04</div>
                <h3 className="font-serif text-3xl md:text-4xl">New Editions</h3>
              </div>
            </Link>
          </ScrollReveal>
        </div>
      </section>

      {/* ────────── CATEGORIES ROW ────────── */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 pb-20 md:pb-24" data-testid="categories-section">
        <ScrollReveal className="flex items-end justify-between gap-4 mb-8 md:mb-10 flex-wrap">
          <div>
            <div className="ie-overline text-brand-accent mb-3">Categories</div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight">Made for the long view.</h2>
          </div>
          <Link to="/shop" className="ie-overline hover:text-brand-accent" data-testid="see-all-categories">View All →</Link>
        </ScrollReveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
          {[
            { label: "Shirts", to: "/shop?type=shirts", img: "https://5.imimg.com/data5/ANDROID/Default/2021/7/ZU/SX/JG/126847924/product-jpeg.jpg" },
            { label: "T-Shirts", to: "/shop?type=tshirts", img: "https://tenaxtees.com/cdn/shop/files/FRT_HHHH_MISP_CC1_1753264036523-1617955812.png?v=1753407779&width=832" },
            { label: "Trousers", to: "/shop?type=trousers", img: "https://oliveplanet.in/cdn/shop/files/IX9_tactical_cargo_trouser_black_1280x.jpg?v=1743965122" },
            { label: "Jackets", to: "/shop?type=jackets", img: "https://img.kwcdn.com/product/fancy/a76d22a0-433f-46f8-8272-63eddfa5a8b0.jpg?imageMogr2/auto-orient%7CimageView2/2/w/800/q/70/format/webp" },
          ].map((c, i) => (
            <ScrollReveal key={c.label} delay={i * 0.08}>
              <Link to={c.to} className="block group" data-testid={`category-${c.label.toLowerCase()}`}>
                <div className="aspect-[3/4] relative overflow-hidden bg-surface">
                  <img src={c.img} alt={c.label} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"/>
                </div>
                <div className="pt-4 flex items-center justify-between">
                  <h3 className="font-serif text-xl">{c.label}</h3>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
                </div>
              </Link>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* ────────── FEATURED PRODUCTS ────────── */}
      <section className="max-w-[1600px] mx-auto px-5 md:px-10 pb-20 md:pb-24" data-testid="featured-products">
        <ScrollReveal className="flex items-end justify-between gap-4 mb-8 md:mb-10 flex-wrap">
          <div>
            <div className="ie-overline text-brand-accent mb-3">The First Look</div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight">New, quietly arrived.</h2>
          </div>
          <Link to="/shop?sort=newest" className="ie-overline hover:text-brand-accent">All New In →</Link>
        </ScrollReveal>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-6" data-testid="featured-grid">
          {products.slice(0, 8).map((p, i) => (
            <ScrollReveal key={p.id} delay={(i % 4) * 0.08}>
              <ProductCard product={p} onQuickView={setQuick}/>
            </ScrollReveal>
          ))}
        </div>
      </section>

      <QuickView product={quick} open={!!quick} onClose={() => setQuick(null)} />

      {/* ────────── TRUST BADGES ────────── */}
      <section className="border-y border-border bg-surface" data-testid="trust-badges">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-12 md:py-14 grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8">
          {[
            { icon: Truck, t: "Complimentary Shipping", s: "On orders over ₹3,500" },
            { icon: RotateCcw, t: "7-Day Atelier Replacment", s: "No questions asked" },
            { icon: Award, t: "Hand-Finished", s: "In our atelier" },
            { icon: Sparkles, t: "Quiet Luxury", s: "Made for the long view" },
          ].map((b, i) => (
            <ScrollReveal key={i} delay={i * 0.08} className="flex items-start gap-4">
              <b.icon className="w-5 h-5 mt-1 text-brand-accent" />
              <div>
                <div className="ie-overline mb-1">{b.t}</div>
                <div className="text-xs text-text-secondary">{b.s}</div>
              </div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* ────────── TESTIMONIAL ────────── */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32" data-testid="testimonials-section">
        <ScrollReveal className="ie-overline text-brand-accent mb-8">Said quietly</ScrollReveal>
        <div className="grid md:grid-cols-3 gap-10 md:gap-12">
          {TESTIMONIALS.map((t, i) => (
            <ScrollReveal key={i} delay={i * 0.12}>
              <div className="text-text-secondary mb-6 font-serif text-2xl sm:text-3xl md:text-[34px] leading-[1.2] text-foreground italic">"{t.quote}"</div>
              <div className="ie-overline">{t.author}</div>
              <div className="text-xs text-text-secondary mt-1">{t.role}</div>
            </ScrollReveal>
          ))}
        </div>
      </section>

      {/* ────────── FAQ ────────── */}
      <section className="border-t border-border" data-testid="faq-section">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-20 md:py-24 grid md:grid-cols-12 gap-10 md:gap-12">
          <ScrollReveal className="md:col-span-4">
            <div className="ie-overline text-brand-accent mb-4">Quietly Asked</div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-tight leading-[1.05]">Notes from the atelier.</h2>
          </ScrollReveal>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible defaultValue="item-0">
              {FAQ.map((f, i) => (
                <AccordionItem key={i} value={`item-${i}`} className="border-b border-border" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="font-serif text-lg sm:text-xl md:text-2xl py-5 md:py-6 hover:no-underline text-left">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-text-secondary leading-relaxed pb-6 max-w-2xl">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </div>
  );
}
